import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Divider from '@material-ui/core/Divider';
import Badge from '@material-ui/core/Badge';
import { makeStyles } from '@material-ui/core/styles';
import TagChip from 'components/TagChip';
import LinearProgressWithLabel from 'components/LinearProgressWithLabel';
import cache from 'utilities/cache';
import { request } from 'utilities/graph';
import { groupPurchaseClientQueryOrderAmount } from 'graphql/mutations';

const getProgressColor = (value, min, max) => {
  if (value >= max) {
    return '#f05a5a'; // red - 達到上限
  } else if (value < min) {
    return '#f8aa5d'; // orange - 未達到最小訂單量
  } else {
    return '#00913A'; // green - 在最小和最大訂單量之間
  }
};

const useStyles = makeStyles((theme) => ({
  mediaContainer: {
    position: 'relative',
  },
  media: {
    height: 150,
    opacity: 0.1,
  },
  imageContainer: {
    position: 'absolute',
    top: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  img: {
    objectFit: 'contain',
    width: '100%',
    height: '100%',
  },
  card: {
    cursor: 'pointer',
    width: '100%',
    height: '100%',
    display: 'flex',
  },
  restaurantInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
  },
  progressContainer: {
    'flex': 1,
    'minWidth': 0,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      flex: '0 0 auto',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  restaurantRulesContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  restaurantRules: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    width: '100%',
    color: 'rgb(166, 133, 2)',
    backgroundColor: 'rgba(244, 228, 137, 0.6)', // Slightly more opaque
    padding: '12px',
    borderRadius: '8px',
    margin: 8,
  },
  progress: {
    'height': 8,
    'borderRadius': 4,
    '& .MuiLinearProgress-bar': {
      backgroundColor: (props) => {
        const { value, min, max } = props;
        return getProgressColor(value, min, max);
      },
    },
  },
  restaurantSectionContainer: {
    position: 'relative',
    padding: 16,
  },
  soldOutOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.54)', // Slightly more opaque
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2, // Ensure it's on top of other content
    textAlign: 'center',
  },
  soldOutText: {
    color: 'rgb(237, 137, 137)', // Red color, consistent with progress bar max
    fontWeight: 'bold',
    fontSize: '1.2rem',
    backgroundColor: 'rgba(231, 177, 177, 0.35)',
    padding: '12px',
    borderRadius: '8px',
  },
  disabledOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.54)', // Greyish overlay
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
    textAlign: 'center',
    cursor: 'not-allowed',
    padding: '12px',
    borderRadius: '8px',
  },
  disabledText: {
    color: 'rgb(100, 100, 100)',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    backgroundColor: 'rgba(180, 180, 180, 0.35)',
    padding: '12px',
    borderRadius: '8px',
  },
  upcomingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.54)', // Light grey, semi-transparent
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
    textAlign: 'center',
    borderRadius: '4px', // Match Card's typical border radius
    cursor: 'not-allowed',
  },
  upcomingText: {
    color: '#00913A', // Darker grey text
    fontWeight: 'bold',
    fontSize: '1rem',
    backgroundColor: 'rgba(229, 248, 191, 0.83)', // Light, slightly transparent background for text
    padding: '8px 12px',
    borderRadius: '6px',
  },
  tagChip: {
    fontSize: '1rem',
    margin: 2,
    fontWeight: 'bold',
  },
}));

export default function RestaurantMeals({ restaurant, onMealClick, filters, addedMeals = [], isDisabled, disabledReason }) {
  const [filteredMeals, setFilteredMeals] = useState([]);
  const [receivedOrderAmount, setReceivedOrderAmount] = useState(0);

  const classes = useStyles({
    value: receivedOrderAmount,
    min: restaurant.minOrderQuantity,
    max: restaurant.maxOrderQuantity,
  });

  useEffect(() => {
    const fetchData = async () => {
      global.logger.debug('filters', filters);

      const filtered = restaurant.meals.filter((meal) => {
        let shouldDisplay = true;
        Object.keys(filters).forEach((key) => {
          if (key === 'text') {
            shouldDisplay = [
              meal.name, meal.description,
              meal.tagsString,
            ].some((value) => {
              return value ? value.toLowerCase().includes(filters[key]) : false;
            });
            return;
          }
          const targetValues = filters[key].filter((x) => x);
          if (targetValues.length === 0) {
            return;
          }
          global.logger.debug('key', key, targetValues);
          // compare key to subcategory
          const result = meal.tags.items.some((item) => {
            const tagItem = item[Object.keys(item)[0]];
            if (tagItem.subcategory === key) {
              return targetValues.includes(tagItem.label);
            } else {
              return false;
            }
          });
          if (!result) {
            shouldDisplay = false;
          }
        });

        return shouldDisplay;
      });

      setFilteredMeals(filtered);

      // 查詢已收到的訂單數量
      const { date, timeSlot } = cache.get('public:menu-elder');

      const startTime = timeSlot.split('-')[0]; // 取得開始時間
      const hour = parseInt(startTime.split(':')[0], 10);
      const { data: { groupPurchaseClientQueryOrderAmount: result } } = await request(groupPurchaseClientQueryOrderAmount, {
        input: {
          restaurantId: restaurant.id,
          date: date,
          mealSlot: hour < 15 ? 'lunch' : 'dinner', // 15:00 之前算午餐，之後算晚餐
        },
      }, 'API_KEY');

      setReceivedOrderAmount(result.totalQuantity);
    };

    fetchData();
  }, [filters, restaurant.id, restaurant.meals, restaurant.minOrderQuantity, restaurant.maxOrderQuantity]);

  if (filteredMeals.length === 0) {
    return <div/>;
  }

  const isSoldOut = typeof restaurant.maxOrderQuantity === 'number' &&
                    restaurant.maxOrderQuantity > 0 &&
                    receivedOrderAmount >= restaurant.maxOrderQuantity;

  let disabledOverlayContent = null;
  if (isDisabled) {
    if (disabledReason === 'minOrderDays') {
      disabledOverlayContent = (
        <Typography variant="h5" component="p" className={classes.disabledText}>
          此餐廳備餐時間較長，請調整取餐日期
        </Typography>
      );
    } else if (disabledReason === 'closedAtTime') {
      disabledOverlayContent = (
        <Typography variant="h5" component="p" className={classes.disabledText}>
          此日期非餐廳營業時間
        </Typography>
      );
    } else { // Generic fallback, though we expect a reason
      disabledOverlayContent = (
        <Typography variant="h5" component="p" className={classes.disabledText}>
          此餐廳暫不開放
        </Typography>
      );
    }
  }

  return <Grid container spacing={2} className={classes.restaurantSectionContainer}>
    {isSoldOut && !isDisabled && (
      <div className={classes.soldOutOverlay}>
        <Typography variant="h5" component="p" className={classes.soldOutText}>
          歐歐～此熱門餐廳已被預訂一空，下次再早點行動吧💪
        </Typography>
      </div>
    )}
    {isDisabled && disabledOverlayContent && (
      <div className={classes.disabledOverlay}>
        {disabledOverlayContent}
      </div>
    )}
    <Grid item xs={12}>
      <div className={classes.restaurantInfo}>
        <Typography variant="h5" component="span" style={{ fontWeight: 'bold' }}>
          {restaurant.name}
        </Typography>
        <div className={classes.progressContainer}>
          <LinearProgressWithLabel
            value={receivedOrderAmount}
            max={restaurant.maxOrderQuantity}
            className={classes.progress}
          />
          <Typography variant="subtitle1" color="textSecondary" align="center" style={{ fontWeight: 'bold' }}>
            {(() => {
              let progressText = `目前已募集 ${receivedOrderAmount} 份`;
              if (restaurant.maxOrderQuantity > 0) {
                progressText += ` ➡️ 目標 ${restaurant.minOrderQuantity} 份`;
              } else {
                progressText += ` (無上限)`;
              }
              if (restaurant.minOrderQuantity > 0 && receivedOrderAmount < restaurant.minOrderQuantity) {
                progressText += ` (還差 ${restaurant.minOrderQuantity - receivedOrderAmount} 份達標)`;
              } else if (restaurant.minOrderQuantity > 0 && receivedOrderAmount >= restaurant.minOrderQuantity) {
                progressText += ` (已達標🎉)`;
              }
              return progressText;
            })()}
          </Typography>
        </div>
      </div>
      <div className={classes.restaurantRulesContainer}>
        {receivedOrderAmount >= restaurant.minOrderQuantity && (
          <Typography variant="text" style={{ color: 'green', fontWeight: 'bold' }}>已達到最低起單量！</Typography>
        )}
        {restaurant.restaurantRules && (
          <p className={classes.restaurantRules}>
            📢 店家的話： {restaurant.restaurantRules}
          </p>
        )}
      </div>
      <Divider />
    </Grid>
    {
      filteredMeals.map((meal) => {
        const { id, imageUrls, name, price, tags } = meal;
        const imgSrc = (imageUrls && imageUrls[0]) || '/images/meal.webp';
        const quantity = (addedMeals.find((addedMeal) => addedMeal.meal.id === id) || {}).quantity;
        const isUpcoming = meal.status === 'upcoming';

        return (
          <Grid container item sm={12} md={6} key={id}>
            <Badge badgeContent={quantity} color="secondary" />
            <Grid item xs={12}>
              <Card
                className={classes.card}
                onClick={() => {
                  if (!isSoldOut && !isDisabled && !isUpcoming) {
                    onMealClick(meal, restaurant, receivedOrderAmount);
                  }
                }}
                style={{
                  position: 'relative', // For overlay positioning
                  cursor: (isSoldOut || isDisabled || isUpcoming) ? 'not-allowed' : 'pointer',
                }}
              >
                <div className={classes.mediaContainer} >
                  <CardMedia
                    className={classes.media}
                    image={imgSrc}
                    title="照片"
                    component="img"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/meal.webp';
                    }}
                  />
                  <div className={classes.imageContainer}>
                    <img className={classes.img}
                      src={imgSrc}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/meal.webp';
                      }}
                    />
                  </div>
                </div>
                <CardContent style={{ position: 'relative', padding: 8 }}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={0}
                  >
                    <Grid item xs={12}>
                      <Typography gutterBottom variant="h5" color="textPrimary">
                        {name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography gutterBottom variant="h6" color="textSecondary" style={{ fontWeight: 'bold' }}>
                        ${price}
                      </Typography>
                    </Grid>
                  </Grid>

                  {tags.items.map(({ tag }, index)=>(
                    <TagChip
                      key={index}
                      tag={tag}
                      className={classes.tagChip}
                    />
                  ))}
                </CardContent>
                {isUpcoming && (
                  <div className={classes.upcomingOverlay}>
                    <Typography variant="body1" component="p" className={classes.upcomingText}>
                      餐點即將上架，敬請期待
                    </Typography>
                  </div>
                )}
              </Card>
            </Grid>
          </Grid>
        );
      })}
  </Grid>;
}
RestaurantMeals.propTypes = {
  restaurant: PropTypes.object.isRequired,
  onMealClick: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  addedMeals: PropTypes.array,
  isDisabled: PropTypes.bool,
  disabledReason: PropTypes.string,
};

